import React, { useState } from "react";
import { DataField } from "../shared/DataField";
import EditControls from "../shared/EditControls";
import clientAxiosInstance from "../../../api/clientAxiosInstance";
import { BANK_ACCOUNT_TYPE_OPTIONS, BANK_LINK_TYPE_OPTIONS } from "../../../constants/accountConstants";
import { canonicalBankAccountType } from "../../../utils/bankAccounts";
import ConfirmDeleteModal from "../../ConfirmDeleteModal";

const ReviewBankAccountsTab = ({
  householdData,
  household_id,
  clientToken,
  isEditing,
  formData,
  onChange,
  onEdit,
  onSave,
  onCancel,
  saving,
  validationErrors,
  onFieldError,
  onDataUpdate,
  onAddBankAccount,
}) => {
  const bankAccounts = householdData?.bank_accounts || [];
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    bankAccountId: null,
  });

  const toggleAccount = (id) => {
    setExpandedAccounts((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const performDelete = async (bankAccountId) => {
    setDeletingId(bankAccountId);
    try {
      await clientAxiosInstance.delete(
        `/client/household/${household_id}/bank-account/${bankAccountId}`,
        { headers: { Authorization: `Bearer ${clientToken}` } },
      );
      if (onDataUpdate) onDataUpdate();
    } catch (err) {
      console.error("Failed to delete bank account:", err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDelete = (bankAccountId) => {
    setConfirmDelete({ open: true, bankAccountId });
  };

  const closeConfirmDelete = () => {
    setConfirmDelete({ open: false, bankAccountId: null });
  };

  const executeConfirmDelete = () => {
    const id = confirmDelete.bankAccountId;
    setConfirmDelete({ open: false, bankAccountId: null });
    if (id) void performDelete(id);
  };

  const getFormValue = (bankAccountId) => (path, fallback) => {
    if (!formData || !formData[bankAccountId]) return fallback;
    const keys = path.split(".");
    let current = formData[bankAccountId];
    for (const key of keys) {
      if (current && current[key] !== undefined) {
        current = current[key];
      } else {
        return fallback;
      }
    }
    return current !== undefined ? current : fallback;
  };

  const handleFieldChange = (bankAccountId) => (fieldPath, value) => {
    if (onChange) onChange(bankAccountId, fieldPath, value);
  };

  const maskAccountNumber = (num) => {
    if (!num) return "N/A";
    const s = String(num);
    if (s.length <= 4) return s;
    return `****${s.slice(-4)}`;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Bank Accounts</h2>
        <EditControls
          isEditing={isEditing}
          onEdit={onEdit}
          onSave={onSave}
          onCancel={onCancel}
          saving={saving}
          disabled={Object.keys(validationErrors || {}).length > 0}
        />
      </div>

      {bankAccounts.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No bank accounts found.</p>
        </div>
      ) : null}

      {bankAccounts.length > 0 && (
        <div className="space-y-3">
          {bankAccounts.map((ba) => (
            <div key={ba.id} className="border border-gray-200 rounded-lg">
              <button
                onClick={() => toggleAccount(ba.id)}
                className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <span className="font-medium text-gray-900">{ba.bank_name || "Bank Account"}</span>
                    <span className="text-sm text-gray-500 ml-2">{maskAccountNumber(ba.account_number)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isEditing && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(ba.id); }}
                      disabled={deletingId === ba.id}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${expandedAccounts[ba.id] ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {expandedAccounts[ba.id] && (
                <div className="p-4 bg-white">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                    <DataField
                      label="Bank Name"
                      value={ba.bank_name || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="bank_name"
                      onFieldChange={handleFieldChange(ba.id)}
                      getFormValue={getFormValue(ba.id)}
                    />
                    <DataField
                      label="Account Name"
                      value={ba.account_name || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="account_name"
                      onFieldChange={handleFieldChange(ba.id)}
                      getFormValue={getFormValue(ba.id)}
                    />
                    <DataField
                      label="Account Number"
                      value={isEditing ? (ba.account_number || "N/A") : maskAccountNumber(ba.account_number)}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="account_number"
                      onFieldChange={handleFieldChange(ba.id)}
                      getFormValue={getFormValue(ba.id)}
                    />
                    <DataField
                      label="Routing Number"
                      value={ba.routing_number || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      type="routing"
                      fieldPath="routing_number"
                      onFieldChange={handleFieldChange(ba.id)}
                      getFormValue={getFormValue(ba.id)}
                    />
                    <DataField
                      label="Account Type"
                      value={canonicalBankAccountType(ba.account_type) || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      options={BANK_ACCOUNT_TYPE_OPTIONS.map(o => o.label)}
                      fieldPath="account_type"
                      onFieldChange={handleFieldChange(ba.id)}
                      getFormValue={getFormValue(ba.id)}
                    />
                    <DataField
                      label="Link Type"
                      value={ba.link_type || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      options={BANK_LINK_TYPE_OPTIONS}
                      fieldPath="link_type"
                      onFieldChange={handleFieldChange(ba.id)}
                      getFormValue={getFormValue(ba.id)}
                    />
                    <div className="py-2">
                      <span className="text-xs font-medium text-gray-500 block mb-1.5">Account Category</span>
                      {isEditing ? (
                        <div className="flex bg-gray-100 rounded-lg p-1 w-full md:w-52">
                          <button
                            type="button"
                            onClick={() => handleFieldChange(ba.id)("is_personal", true)}
                            className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                              (getFormValue(ba.id)("is_personal", ba.is_personal) ?? true)
                                ? "bg-white text-emerald-700 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            Personal
                          </button>
                          <button
                            type="button"
                            onClick={() => handleFieldChange(ba.id)("is_personal", false)}
                            className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                              !(getFormValue(ba.id)("is_personal", ba.is_personal) ?? true)
                                ? "bg-white text-emerald-700 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            Business
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-900">
                          {ba.is_personal === true ? "Personal" : ba.is_personal === false ? "Business" : "N/A"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {onAddBankAccount && (
        <button
          type="button"
          onClick={onAddBankAccount}
          className="mt-4 w-full py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 font-semibold shadow-md shadow-emerald-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Bank Account
        </button>
      )}

      <ConfirmDeleteModal
        isOpen={confirmDelete.open}
        onClose={closeConfirmDelete}
        title="Delete bank account?"
        message="This permanently removes this bank account from the household. This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={executeConfirmDelete}
      />
    </div>
  );
};

export default ReviewBankAccountsTab;
